import React, {useEffect, useMemo} from "react";
import {ColumnDef, Row} from "@tanstack/react-table";
import {nameof} from "ts-simple-nameof";
import {DashboardListForloebModel} from "./models/DashboardListForloebModel";
import {
    FilterOptions,
    InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import { getCellTooltipId } from 'core/components/reactTable/ReactTableHelpers';
import { multiSelectFilterFn } from 'core/components/reactTable/ReactTableHelpers';
import {TableProgressCell} from "../../../core/components/reactTable/tableCells/tableProgressCell";
import {TableGodkendtCell} from "../../../core/components/reactTable/tableCells/tableGodkendtCell";
import useDashboardList from "./useDashboardList";
import {VejlederCell} from "./vejlederCell";
import {VejledningStatusCell} from "./vejledningStatusCell";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {VejledningDaysSinceLastCell} from "./vejledningDaysSinceLastCell";
import ReactSyncTable from "../../../core/components/reactTable/ReactSyncTable";
import {useHistory, useLocation} from "react-router-dom";
import {DashboardColors} from "../models/DashboardColors";
import {RoutePaths} from "../../../infrastructure/routes";
import {DashboardFilterModel} from "../models/DashboardFilter";
import {EmailConstants} from "../../../core/components/mail/emailConstants";

type DashboardListProps = {
    filter: DashboardFilterModel;
    includeOrlov?: boolean;
    onMaxEmailAmountExceededChange?: (exceeded: boolean) => void;
}

export const DashboardList = ({ filter, includeOrlov = false, onMaxEmailAmountExceededChange }: DashboardListProps) => {
    const {forloebList, isLoading} = useDashboardList();
    const location = useLocation();
    const history = useHistory();
    // Capture initialFilter once on mount so re-renders don't keep re-applying it.
    const initialFilter = useMemo(() => location.state as InitialFilterModel[], []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear location.state after consuming so navigating back to this route
    // does not re-apply the graph-supplied filter (overrides cleared localStorage filters).
    useEffect(() => {
        if (location.state) {
            history.replace({...location, state: undefined});
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onMaxEmailAmountExceededChange?.(forloebList.length > EmailConstants.maxAmount);
    }, [forloebList.length, onMaxEmailAmountExceededChange]);

    const columns: ColumnDef<DashboardListForloebModel>[] = useMemo(() => [

        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.brugernavn),
            header: Localizer.global_name(),
            cell: info => (
                <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(RoutePaths.Forloeb([info.row.original.brugerId, info.row.original.forloebId]).url);
                    }}
                >
                    {info.getValue() as string}
                </button>
            ),
            id: nameof<DashboardListForloebModel>(x => x.brugernavn).toLowerCase(),
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.vejlederNavne),
            header: Localizer.global_vejleder(),
            id: nameof<DashboardListForloebModel>(x => x.vejlederNavne).toLowerCase(),
            cell: info => <VejlederCell vejledernames={info.getValue() as string[]}/>,
            aggregationFn: undefined,
            filterFn: (row, columnId, filterValue) => {
                const vejlederNames = row.getValue(columnId) as string[];
                if (!filterValue) return true;
                const searchValue = filterValue.toLowerCase();
                return vejlederNames.some(name => name.toLowerCase().includes(searchValue));
            },
            sortingFn: (rowA, rowB, columnId) => {
                const namesA = rowA.getValue(columnId) as string[];
                const namesB = rowB.getValue(columnId) as string[];
                const firstNameA = namesA[0] || '';
                const firstNameB = namesB[0] || '';
                return firstNameA.localeCompare(firstNameB);
            },
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.uddannelsestype),
            header: Localizer.global_uddannelsestrin(),
            id: nameof<DashboardListForloebModel>(x => x.uddannelsestype).toLowerCase(),
            cell: info => info.getValue(),
            aggregationFn: undefined,
            sortDescFirst: true,
            filterFn: multiSelectFilterFn,
            meta: {
                filterVariant: 'multiselect',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.specialeNavn),
            header: Localizer.global_speciale(),
            id: nameof<DashboardListForloebModel>(x => x.specialeNavn).toLowerCase(),
            cell: info => info.getValue(),
            aggregationFn: undefined,
            filterFn: multiSelectFilterFn,
            meta: {
                filterVariant: 'multiselect',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.uddannelsesstedNavn),
            header: Localizer.global_uddannelsessted(),
            id: nameof<DashboardListForloebModel>(x => x.uddannelsesstedNavn).toLowerCase(),
            cell: info => info.getValue(),
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.mdrOgDageTilbage),
            header: Localizer.dashboard_tidTilbage(),
            cell: info => `${(info.getValue() as number[])[0]}m ${(info.getValue() as number[])[1]}d`,
            id: nameof<DashboardListForloebModel>(x => x.mdrOgDageTilbage).toLowerCase(),
            meta: {
                filterVariant: 'range',
            },
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId) as [number | undefined, number | undefined];
                return (Number(value[0]) <= Number(filterValue[0]) || !filterValue[0])
                    && (Number(value[1]) <= Number(filterValue[1]) || !filterValue[1]);
            },
            sortingFn: (rowA, rowB, columnId) => {
                const valueA = rowA.getValue(columnId) as [number, number];
                const valueB = rowB.getValue(columnId) as [number, number];
                // Sortér dage først og så mdr.
                if (valueA[0] !== valueB[0])
                    return valueA[0] - valueB[0];
                return valueA[1] - valueB[1];
            },

        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.godkendt),
            header: Localizer.global_tidGodkendt(),
            cell: (info) => <TableGodkendtCell godkendt={info.getValue() as boolean}/>,
            id: nameof<DashboardListForloebModel>(x => x.godkendt).toLowerCase(),
            meta: {
                filterVariant: 'select',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.evalueret),
            header: Localizer.global_evalueret(),
            id: nameof<DashboardListForloebModel>(x => x.evalueret).toLowerCase(),
            cell: (info) => <TableGodkendtCell godkendt={info.getValue() as boolean}/>,
            meta: {
                filterVariant: 'select',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.startDato),
            header: Localizer.startdato(),
            id: nameof<DashboardListForloebModel>(x => x.startDato).toLowerCase(),
            cell: (info) => (info.getValue() as Date).dateWithoutTimeFormat(true),
            aggregationFn: undefined,
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.slutDato),
            header: Localizer.slutdato(),
            id: nameof<DashboardListForloebModel>(x => x.slutDato).toLowerCase(),
            cell: (info) => (info.getValue() as Date).dateWithoutTimeFormat(true),
            aggregationFn: undefined,
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.forloebProgress),
            header: Localizer.dashboard_forloebProgress(),
            id: nameof<DashboardListForloebModel>(x => x.forloebProgress).toLowerCase(),
            cell: (info) => <TableProgressCell value={info.getValue() as number}
                                               color={DashboardColors.ForloebProgressColor}/>,
            aggregationFn: undefined,
            sortDescFirst: false,
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.kompetenceProgress),
            header: Localizer.global_godkendteKompetencer(),
            id: nameof<DashboardListForloebModel>(x => x.kompetenceProgress).toLowerCase(),
            cell: (info) => <TableProgressCell value={info.getValue() as number}/>,
            aggregationFn: undefined,
            sortDescFirst: false,
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.antalKlarmeldteKompetencer),
            header: Localizer.dashboard_klarmeldteKompetencer(),
            id: nameof<DashboardListForloebModel>(x => x.antalKlarmeldteKompetencer).toLowerCase(),
            cell: (info) => info.getValue() as number,
            aggregationFn: undefined,
            sortDescFirst: false,
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteKompetenceAktivitet),
            header: Localizer.dashboard_kompetenceAktivitet(),
            id: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteKompetenceAktivitet).toLowerCase(),
            cell: (info) => <VejledningDaysSinceLastCell days={info.getValue() as number | undefined}/>,
            aggregationFn: undefined,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId) as number | undefined;
                if (filterValue?.[0] === -1 && filterValue?.[1] === -1)
                    return value === undefined;
                if (filterValue?.[0] === undefined && filterValue?.[1] === undefined)
                    return true;
                return (value !== undefined)
                    && (filterValue[0] === undefined || value >= filterValue[0])
                    && (filterValue[1] === undefined || value <= filterValue[1]);
            },
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteVejledersamtale),
            header: Localizer.dashboard_sidsteVejledersamtale(),
            id: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteVejledersamtale).toLowerCase(),
            cell: (info) => <VejledningDaysSinceLastCell days={info.getValue() as number | undefined}/>,
            aggregationFn: undefined,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId) as number | undefined;
                if (filterValue?.[0] === -1 && filterValue?.[1] === -1)
                    return value === undefined;
                if (filterValue?.[0] === undefined && filterValue?.[1] === undefined)
                    return true;
                return (value !== undefined)
                    && (filterValue[0] === undefined || value >= filterValue[0])
                    && (filterValue[1] === undefined || value <= filterValue[1]);
            },
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.introVejledersamtaleStatus),
            header: Localizer.dashboard_introsamtale(),
            id: nameof<DashboardListForloebModel>(x => x.introVejledersamtaleStatus).toLowerCase(),
            aggregationFn: undefined,
            cell: info => <VejledningStatusCell status={info.getValue() as string}
                                                afholdDato={info.row.original.introSamtaleAfholdtDato}
                                                cellId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            filterFn: multiSelectFilterFn,
            meta: {filterVariant: 'multiselect'},
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.justerendeVejledersamtaleStatus),
            header: Localizer.dashboard_justerendesamtale(),
            id: nameof<DashboardListForloebModel>(x => x.justerendeVejledersamtaleStatus).toLowerCase(),
            cell: (info) => <VejledningStatusCell status={info.getValue() as string}
                                                  afholdDato={info.row.original.introSamtaleAfholdtDato}
                                                  cellId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
            filterFn: multiSelectFilterFn,
            meta: {
                filterVariant: 'multiselect',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.afsluttendeVejledersamtaleStatus),
            header: Localizer.dashboard_afsluttendesamtale(),
            id: nameof<DashboardListForloebModel>(x => x.afsluttendeVejledersamtaleStatus).toLowerCase(),
            cell: (info) => <VejledningStatusCell status={info.getValue() as string}
                                                  afholdDato={info.row.original.introSamtaleAfholdtDato}
                                                  cellId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
            filterFn: multiSelectFilterFn,
            meta: {
                filterVariant: 'multiselect',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.antalJusterendeVejledersamtaler),
            header: Localizer.dashboard_justerendeVejledersamtaleAmount(),
            id: nameof<DashboardListForloebModel>(x => x.antalJusterendeVejledersamtaler).toLowerCase(),
            cell: (info) => info.getValue() as number,
            aggregationFn: undefined,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId) as number | undefined;
                if (filterValue?.[0] === undefined && filterValue?.[1] === undefined)
                    return true;
                return (value !== undefined)
                    && (filterValue[0] === undefined || value >= filterValue[0])
                    && (filterValue[1] === undefined || value <= filterValue[1]);
            },
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.foersteUddannelsesplanStatus),
            header: Localizer.dashboard_uddannelsesplan(),
            id: nameof<DashboardListForloebModel>(x => x.foersteUddannelsesplanStatus).toLowerCase(),
            cell: (info) => <VejledningStatusCell status={info.getValue() as string} cellId={info.cell.id}/>,
            aggregationFn: undefined,
            filterFn: multiSelectFilterFn,
            meta: {
                filterVariant: 'multiselect',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteUddannelsesplan),
            header: Localizer.dashboard_sidsteUddannelsesplan(),
            id: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteUddannelsesplan).toLowerCase(),
            cell: (info) => <VejledningDaysSinceLastCell days={info.getValue() as number | undefined}/>,
            aggregationFn: undefined,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId) as number | undefined;
                if (filterValue?.[0] === -1 && filterValue?.[1] === -1)
                    return value === undefined;
                if (filterValue?.[0] === undefined && filterValue?.[1] === undefined)
                    return true;
                return (value !== undefined)
                    && (filterValue[0] === undefined || value >= filterValue[0])
                    && (filterValue[1] === undefined || value <= filterValue[1]);
            },
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListForloebModel>(x => x.antalUddannelsesplaner),
            header: Localizer.dashboard_uddannelsesplanerAmount(),
            id: nameof<DashboardListForloebModel>(x => x.antalUddannelsesplaner).toLowerCase(),
            cell: (info) => info.getValue() as number,
            aggregationFn: undefined,
            meta: {
                filterVariant: 'range',
            },
        },
        ...(includeOrlov ? [{
            accessorKey: nameof<DashboardListForloebModel>(x => x.fravaer),
            header: Localizer.Fravaer(),
            cell: (info: any) => info.getValue() ? Localizer.global_ja() : Localizer.global_nej(),
            id: nameof<DashboardListForloebModel>(x => x.fravaer).toLowerCase(),
            meta: {
                filterVariant: 'select' as const,
            },
        }] : []),
    ], [includeOrlov, history]);

    const filterOptions: FilterOptions = {
        pageSizeOptions: [10, 25, 50, 100],
        hidePageSizeFilter: false,
        hideColumnSettingsButton: false,
        initialSortColumn: nameof<DashboardListForloebModel>(x => x.brugernavn),
        initialFilter: initialFilter,
    };

    const rowColorCallback = (row: Row<DashboardListForloebModel>): string => {
        const fravaer = row.original.fravaer;
        return fravaer ? "#ffeeee" : "";
    };

    return (
        <>
        <ReactSyncTable
            tableId="dashboardList"
            title={Localizer.global_listview()}
            data={forloebList}
            columns={columns}
            isLoading={isLoading}
            filterOptions={filterOptions}
            rowColorCallback={rowColorCallback}
        />
        </>);
}
